import { ApiError } from '@/utils/fetch.client';

export interface PullRequestFailure {
  /** One-line headline — the server's own error, or a generic fallback. */
  message: string;
  /** What to do about it, for the failures we recognise. */
  hint: string | null;
  /**
   * The server's `details`, verbatim: git's stderr for a failed push, or the provider's
   * message. The headline alone is useless here — `AppError::bad_request` reduces every
   * push failure to "Invalid request".
   */
  details: string | null;
  requestId: string | null;
  /** Whether retrying the identical request could plausibly work. */
  retryable: boolean;
}

/**
 * The push/PR failures worth recognising, and what the user can do next. Matched against the
 * headline and the details together, because which of the two carries the signal depends on
 * where the flow broke: a rejected push is git's stderr in `details`, while a provider refusal
 * is the headline.
 */
const HINTS: { match: RegExp; hint: string }[] = [
  {
    match: /\[rejected]|fetch first|non-fast-forward/i,
    hint: 'The remote branch has commits the sandbox does not. Run `git pull --rebase` in the Terminal tab, then create the pull request again.',
  },
  {
    match: /could not read Username|Authentication failed|terminal prompts disabled|invalid username or password/i,
    hint: 'The sandbox has no push credentials for this remote. Reconnect the provider under Settings → Integrations, then start a new session.',
  },
  {
    match: /protected branch|GH006|pre-receive hook declined/i,
    hint: 'The remote refuses this push because the branch is protected. Move the work to a separate branch and open the pull request from that.',
  },
  {
    match: /pull request already exists|A pull request for/i,
    hint: 'A pull request for this branch already exists — open it on the provider rather than creating another.',
  },
  {
    match: /works directly on|head and base are the same/i,
    hint: 'A pull request needs two different branches. Create one in the Terminal tab and commit to that.',
  },
  {
    match: /no commits between/i,
    hint: 'The base branch already contains every commit on this branch, so there is nothing to review.',
  },
  {
    match: /has no known default branch|no provider path/i,
    hint: 'The repository record is missing its provider details. Re-add the repository under Settings → Repositories.',
  },
];

/** Turn whatever `createPullRequest` threw into something renderable and actionable. */
export function describePullRequestFailure(error: unknown): PullRequestFailure {
  if (error instanceof ApiError) {
    const haystack = `${error.message}\n${error.details ?? ''}`;
    return {
      message: error.message,
      hint: HINTS.find(({ match }) => match.test(haystack))?.hint ?? null,
      details: error.details,
      requestId: error.requestId,
      retryable: error.transient,
    };
  }

  // Never reaches `ApiError`: the request never completed, so retrying is the right advice.
  return {
    message: error instanceof Error ? error.message : 'Failed to open pull request',
    hint: 'The request did not reach the server. Check the connection and try again.',
    details: null,
    requestId: null,
    retryable: true,
  };
}

/**
 * Why this branch cannot be turned into a pull request at all, if it cannot.
 *
 * Checked before the request rather than after: opening a PR from the base branch onto itself
 * fails in the *push*, whose error ("Updates were rejected because the remote contains work you
 * do not have") describes a stale checkout and says nothing about the real problem.
 */
export function pullRequestBlocker(branch: string | null | undefined, baseRef: string): string | null {
  if (!branch) return 'This session has no branch to open a pull request from.';
  if (branch === baseRef) {
    return `This session works directly on \`${baseRef}\`, the base branch. Create a branch in the Terminal tab and commit to that before opening a pull request.`;
  }
  return null;
}
